/**
 * Groups Page - Admin Table View
 * Displays all groups in a paginated, sortable table
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePageTitle } from '@components/layout/MainLayout'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  InputAdornment,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Badge,
} from '@mui/material'
import { Search, Refresh, Visibility } from '@mui/icons-material'
import { socialApi } from '@api/endpoints/social'
import {
  AdminGroupListItem,
  AdminGroupSortField,
  GroupVisibility,
  GroupType,
  getGroupVisibilityName,
} from '@api/types/social'

function getGroupTypeName(type: GroupType): string {
  switch (type) {
    case GroupType.Master:
      return 'Master'
    case GroupType.Temporary:
      return 'Temporary'
    default:
      return 'Unknown'
  }
}

function getVisibilityColor(visibility: GroupVisibility): 'success' | 'warning' | 'info' | 'default' {
  switch (visibility) {
    case GroupVisibility.Public:
      return 'success'
    case GroupVisibility.InviteOnly:
      return 'warning'
    case GroupVisibility.MatchingTagRequired:
      return 'info'
    default:
      return 'default'
  }
}

export function GroupsPage() {
  usePageTitle('Groups')
  const navigate = useNavigate()

  const [groups, setGroups] = useState<AdminGroupListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Pagination
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [totalCount, setTotalCount] = useState(0)

  // Sorting - default is CreatedDate descending
  const [sortBy, setSortBy] = useState<AdminGroupSortField>(AdminGroupSortField.CreatedDate)
  const [sortDescending, setSortDescending] = useState(true)

  // Search
  const [search, setSearch] = useState('')

  const fetchGroups = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await socialApi.getAdminGroups({
        page: page + 1, // API is 1-based
        pageSize,
        sortBy,
        sortDescending,
        search: search || undefined,
      })
      setGroups(response.items || [])
      setTotalCount(response.totalCount || 0)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch groups')
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, sortBy, sortDescending, search])

  useEffect(() => {
    fetchGroups()
  }, [fetchGroups])

  const handleSort = (field: AdminGroupSortField) => {
    if (sortBy === field) {
      setSortDescending(!sortDescending)
    } else {
      setSortBy(field)
      setSortDescending(false)
    }
    setPage(0)
  }

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleSearchKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      setPage(0)
      fetchGroups()
    }
  }

  const handleRowClick = (group: AdminGroupListItem) => {
    navigate(`/groups/${group.id}`)
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A'
    const date = new Date(dateStr)
    return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString()
  }

  const renderSortableHeader = (field: AdminGroupSortField, label: string) => (
    <TableSortLabel
      active={sortBy === field}
      direction={sortBy === field ? (sortDescending ? 'desc' : 'asc') : 'asc'}
      onClick={() => handleSort(field)}
      sx={{
        color: 'white !important',
        '& .MuiTableSortLabel-icon': { color: 'white !important' },
      }}
    >
      {label}
    </TableSortLabel>
  )

  return (
    <Box>
      {/* Search and Actions */}
      <Paper sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search groups..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            size="small"
            sx={{ minWidth: 300, flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchGroups}
          >
            Refresh
          </Button>
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                  Name
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                  Creator
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                  Visibility
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                  Type
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">
                  {renderSortableHeader(AdminGroupSortField.MemberCount, 'Members')}
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">
                  {renderSortableHeader(AdminGroupSortField.DiscussionCount, 'Discussions')}
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">
                  Adventures
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">
                  Events
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">
                  Pending
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                  {renderSortableHeader(AdminGroupSortField.CreatedDate, 'Created')}
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : groups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No groups found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                groups.map((group) => (
                  <TableRow
                    key={group.id}
                    hover
                    onClick={() => handleRowClick(group)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'action.hover' },
                      opacity: group.isArchived ? 0.6 : 1,
                    }}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {group.name}
                          {group.isArchived && (
                            <Chip
                              label="Archived"
                              size="small"
                              sx={{ ml: 1 }}
                              color="default"
                            />
                          )}
                        </Typography>
                        {group.description && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: 250,
                            }}
                          >
                            {group.description}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{group.creatorDisplayName}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getGroupVisibilityName(group.visibility)}
                        size="small"
                        color={getVisibilityColor(group.visibility)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{getGroupTypeName(group.groupType)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{group.memberCount}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{group.discussionCount}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{group.adventureCount}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{group.eventCount}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      {group.pendingRequestCount > 0 ? (
                        <Badge badgeContent={group.pendingRequestCount} color="warning">
                          <Box sx={{ width: 24, height: 24 }} />
                        </Badge>
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(group.createdDate)}</Typography>
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="small"
                        startIcon={<Visibility />}
                        onClick={() => handleRowClick(group)}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={pageSize}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handlePageSizeChange}
        />
      </Paper>
    </Box>
  )
}
